import type { FloatingModelNodeData } from '../nodes/FloatingModelNode';
import type { HostedAgentConnection } from '@/types/hostedAgent';

/** The subset of an agent node's data needed to build its canvas model node. */
export interface AgentModelNodeInput {
  /** Action name of the parent agent node. */
  agentName: string;
  /** Model deployment for native/managed agents (from inputs.parameters). */
  modelDeployment?: string | undefined;
  /** Connection reference name (model1); for hosted agents this is the hosted-agent key. */
  connectionName?: string | undefined;
  /** Agent model type, e.g. 'FoundryHostedAgent'. */
  agentModelType?: string | undefined;
}

/**
 * Resolves the canvas "model" node data shown below an agent node.
 *
 * Native/coding/Foundry agents carry their model in `inputs.parameters`
 * (surfaced as `modelDeployment`). A FoundryHostedAgent instead stores its
 * model in the hosted-agent connection record
 * (`connections.json → hostedAgentConnections[key].configuration.modelDeployment`),
 * so we look it up via the agent's connection name. This lets the hosted-agent
 * model node display the actual model (e.g. `gpt-5`) instead of an empty pill or
 * the raw connection key — matching the coding-agent behavior (issue 3435).
 *
 * The connection name is preserved for the node's tooltip.
 */
export function resolveAgentModelNodeData(
  agent: AgentModelNodeInput,
  hostedAgentConnections: Record<string, HostedAgentConnection> | undefined,
): FloatingModelNodeData {
  const isHostedAgent = agent.agentModelType === 'FoundryHostedAgent';

  const hostedModelDeployment =
    isHostedAgent && agent.connectionName
      ? hostedAgentConnections?.[agent.connectionName]?.configuration?.modelDeployment
      : undefined;

  const effectiveModelDeployment = isHostedAgent ? hostedModelDeployment : agent.modelDeployment;
  const isConfigured = !!effectiveModelDeployment;

  return {
    label: isConfigured
      ? (effectiveModelDeployment?.split('/').pop() ?? 'Model')
      : isHostedAgent
        ? (agent.connectionName ?? 'Hosted Agent')
        : 'Configure',
    modelDeployment: effectiveModelDeployment ?? '',
    parentAgentName: agent.agentName,
    ...(agent.connectionName ? { connectionName: agent.connectionName } : {}),
    isConfigured,
  };
}
